/**
 * Pure layout math for the canvas selection overlay: the selection bar (breadcrumb merged in)
 * that floats over the canvas iframe next to the selected component's outline. Every rect here
 * lives in the designer window's coordinate space (px) unless documented otherwise — nothing
 * in this module reads the DOM, so it stays trivially unit-testable and framework-agnostic.
 */
import { applyHysteresis, IBarCandidate } from "./hysteresis";

export interface IRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ISize {
  width: number;
  height: number;
}

export interface IFrameTransform {
  frameLeft: number;
  frameTop: number;
  scale: number;
}

export type BarSide = "below" | "above" | "stickyBottom" | "stickyTop";
export type OverlayMode =
  | "anchored"
  | "offscreenAbove"
  | "offscreenBelow"
  | "offscreenLeft"
  | "offscreenRight"
  | "hidden";

/** Size of the small edge indicator shown when the selection has scrolled out of view. */
export const EDGE_CHIP_SIZE: ISize = { width: 32, height: 32 };

/** Inset, in px, between an edge chip and the visible frame's border. */
const EDGE_CHIP_INSET: number = 8;

export interface IPlacementInput {
  /** Selection outline, already mapped into window coordinates. */
  anchor: IRect;
  /** Visible part of the canvas scroll frame, in window coordinates. */
  visible: IRect;
  /** Area the overlay may occupy (the canvas region between side panels), already inset. */
  safe: IRect;
  /** Measured bar size (the breadcrumb is merged into it). */
  bar: ISize;
  /** Distance kept between the anchor and the bar. */
  gap: number;
  /** Previous frame's bar side, for hysteresis. */
  prevBarSide: BarSide | null;
  /** Slack band, in px, inside which the previous bar side is preferred over a newly-fitting one. */
  hysteresisBand: number;
  /**
   * How far the canvas can still scroll. With it, a selection no scroll position can bring into view gets the
   * bar pinned to the edge of the visible area instead of an edge chip that could never scroll to it.
   * Omitted: the edge chip, as before.
   */
  reach?: IScrollReach;
}

/** How far, in window px, the canvas scroll frame can still scroll toward the top and toward the bottom. */
export interface IScrollReach {
  towardTop: number;
  towardBottom: number;
}

export interface IPlacement {
  mode: OverlayMode;
  barSide: BarSide;
  bar: IRect | null;
  /** Small chip shown at the visible edge nearest the anchor when the overlay's mode is offscreen*. */
  edgeChip: IRect | null;
}

/** Maps an iframe-viewport rect into the designer window's coordinate space. */
export function toParentRect(r: IRect, t: IFrameTransform): IRect {
  return {
    x: t.frameLeft + r.x * t.scale,
    y: t.frameTop + r.y * t.scale,
    width: r.width * t.scale,
    height: r.height * t.scale
  };
}

/**
 * Shifts a rect measured at one scroll position so it still lines up after the scroll frame has
 * moved on, by the delta between the position it was measured at and the current ("live") one.
 * A fixed-position rect never moves with scroll, so it passes through unchanged.
 */
export function compensateScroll(
  r: IRect,
  measuredTop: number,
  measuredLeft: number,
  liveTop: number,
  liveLeft: number,
  isFixed: boolean
): IRect {
  if (isFixed) {
    return { x: r.x, y: r.y, width: r.width, height: r.height };
  }
  const deltaTop: number = measuredTop - liveTop;
  const deltaLeft: number = measuredLeft - liveLeft;
  return { x: r.x + deltaLeft, y: r.y + deltaTop, width: r.width, height: r.height };
}

/** The scroll frame's remaining room either way, mapped through the canvas zoom (`scale`). */
export function scrollReach(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
  scale: number
): IScrollReach {
  const maxScrollTop: number = Math.max(0, scrollHeight - clientHeight);
  const top: number = Math.min(Math.max(0, scrollTop), maxScrollTop);
  return { towardTop: top * scale, towardBottom: (maxScrollTop - top) * scale };
}

/** Smallest extent, in px, the overlay treats an anchor as having on either axis. */
export const MIN_ANCHOR_EXTENT: number = 4;

/** Grows a span to `MIN_ANCHOR_EXTENT` around its centre, then keeps it inside `[min, max]` if it started there. */
function growSpan(pos: number, size: number, min: number, max: number): [number, number] {
  const grown: number = Math.max(size, MIN_ANCHOR_EXTENT);
  const centred: number = pos - (grown - size) / 2;
  const startedInside: boolean = pos >= min && pos + size <= max;
  return [startedInside ? clampAxis(centred, grown, min, max) : centred, grown];
}

/**
 * Grows a thin or empty rect to `MIN_ANCHOR_EXTENT` on each short axis, keeping its centre — except that a
 * rect lying inside `bounds` on an axis stays inside it: a hairline flush with the visible area's top edge
 * (a fixed header's 1px band) grown around its centre would poke out, leave a sub-`MIN_ANCHOR_EXTENT`
 * overlap and read as off-screen, parking the bar while the line sits in plain view.
 */
export function withMinimumExtent(r: IRect, bounds?: IRect): IRect {
  const xMin: number = bounds ? bounds.x : -Infinity;
  const xMax: number = bounds ? bounds.x + bounds.width : Infinity;
  const yMin: number = bounds ? bounds.y : -Infinity;
  const yMax: number = bounds ? bounds.y + bounds.height : Infinity;
  const xSpan: [number, number] = growSpan(r.x, r.width, xMin, xMax);
  const ySpan: [number, number] = growSpan(r.y, r.height, yMin, yMax);
  return { x: xSpan[0], y: ySpan[0], width: xSpan[1], height: ySpan[1] };
}

/** Strict rect intersection: touching-but-not-overlapping edges are not considered an overlap. */
export function intersect(a: IRect, b: IRect): IRect | null {
  const x1: number = Math.max(a.x, b.x);
  const y1: number = Math.max(a.y, b.y);
  const x2: number = Math.min(a.x + a.width, b.x + b.width);
  const y2: number = Math.min(a.y + a.height, b.y + b.height);
  const width: number = x2 - x1;
  const height: number = y2 - y1;
  if (width <= 0 || height <= 0) {
    return null;
  }
  return { x: x1, y: y1, width, height };
}

/** Clamps a span of `size` starting at `pos` into `[min, max]`; an oversized span pins to `min`. */
function clampAxis(pos: number, size: number, min: number, max: number): number {
  if (size > max - min) {
    return min;
  }
  if (pos < min) {
    return min;
  }
  const maxPos: number = max - size;
  if (pos > maxPos) {
    return maxPos;
  }
  return pos;
}

/** Clamps a horizontal span into `safe`'s x-axis; a span wider than `safe` pins to `safe.x`. */
export function clampX(left: number, width: number, safe: IRect): number {
  return clampAxis(left, width, safe.x, safe.x + safe.width);
}

/** Clamps a vertical span into `safe`'s y-axis; a span taller than `safe` pins to `safe.y`. */
export function clampY(top: number, height: number, safe: IRect): number {
  return clampAxis(top, height, safe.y, safe.y + safe.height);
}

/**
 * The largest height a popover anchored to `[anchorTop, anchorBottom]` can use without leaving
 * `safe`, preferring whichever side (above or below the anchor) has more room, capped at `cap`
 * and never smaller than a usable 120px floor.
 */
export function popoverMaxHeight(
  anchorBottom: number,
  anchorTop: number,
  safe: IRect,
  gap: number,
  cap: number
): number {
  const spaceBelow: number = safe.y + safe.height - anchorBottom;
  const spaceAbove: number = anchorTop - safe.y;
  const best: number = Math.max(spaceBelow, spaceAbove) - gap;
  return Math.max(120, Math.min(cap, best));
}

interface IEdgeChipResult {
  mode: OverlayMode;
  edgeChip: IRect | null;
}

function placeEdgeChip(anchor: IRect, visible: IRect, safe: IRect): IEdgeChipResult {
  const anchorCenterX: number = anchor.x + anchor.width / 2;
  const anchorCenterY: number = anchor.y + anchor.height / 2;
  const visibleBottom: number = visible.y + visible.height;
  const visibleRight: number = visible.x + visible.width;

  if (anchorCenterY < visible.y) {
    const x: number = clampX(anchorCenterX - EDGE_CHIP_SIZE.width / 2, EDGE_CHIP_SIZE.width, safe);
    const y: number = visible.y + EDGE_CHIP_INSET;
    return { mode: "offscreenAbove", edgeChip: { x, y, width: EDGE_CHIP_SIZE.width, height: EDGE_CHIP_SIZE.height } };
  }
  if (anchorCenterY > visibleBottom) {
    const x: number = clampX(anchorCenterX - EDGE_CHIP_SIZE.width / 2, EDGE_CHIP_SIZE.width, safe);
    const y: number = visibleBottom - EDGE_CHIP_INSET - EDGE_CHIP_SIZE.height;
    return { mode: "offscreenBelow", edgeChip: { x, y, width: EDGE_CHIP_SIZE.width, height: EDGE_CHIP_SIZE.height } };
  }
  if (anchorCenterX < visible.x) {
    const yMin: number = Math.max(visible.y, safe.y);
    const yMax: number = Math.min(visibleBottom, safe.y + safe.height);
    const y: number = clampAxis(anchorCenterY - EDGE_CHIP_SIZE.height / 2, EDGE_CHIP_SIZE.height, yMin, yMax);
    const x: number = visible.x + EDGE_CHIP_INSET;
    return { mode: "offscreenLeft", edgeChip: { x, y, width: EDGE_CHIP_SIZE.width, height: EDGE_CHIP_SIZE.height } };
  }
  if (anchorCenterX > visibleRight) {
    const yMin: number = Math.max(visible.y, safe.y);
    const yMax: number = Math.min(visibleBottom, safe.y + safe.height);
    const y: number = clampAxis(anchorCenterY - EDGE_CHIP_SIZE.height / 2, EDGE_CHIP_SIZE.height, yMin, yMax);
    const x: number = visibleRight - EDGE_CHIP_INSET - EDGE_CHIP_SIZE.width;
    return { mode: "offscreenRight", edgeChip: { x, y, width: EDGE_CHIP_SIZE.width, height: EDGE_CHIP_SIZE.height } };
  }
  // The anchor's center is actually inside `visible` in both axes — the tiny/absent overlap is
  // a rounding sliver, not a real off-screen direction. Nothing directional to point at.
  return { mode: "hidden", edgeChip: null };
}

/**
 * Whether some scroll position shows at least `MIN_ANCHOR_EXTENT` of the anchor's height inside `visible`:
 * scrolling toward the top moves the content down by up to `towardTop`, toward the bottom up by up to
 * `towardBottom`.
 */
function canScrollIntoView(anchor: IRect, visible: IRect, reach: IScrollReach): boolean {
  const lowestShift: number = Math.max(-reach.towardBottom, visible.y + MIN_ANCHOR_EXTENT - (anchor.y + anchor.height));
  const highestShift: number = Math.min(reach.towardTop, visible.y + visible.height - MIN_ANCHOR_EXTENT - anchor.y);
  return lowestShift <= highestShift;
}

/**
 * A selection inside the visible area's horizontal span that no vertical scroll can bring into view: a
 * page-level report component under a fixed header or footer band, which covers the page's first and last strip
 * at every scroll position. An edge chip would point at a place the canvas can never scroll to.
 */
function isBehindFixedBand(anchor: IRect, visible: IRect, reach: IScrollReach): boolean {
  const horizontalOverlap: number =
    Math.min(anchor.x + anchor.width, visible.x + visible.width) - Math.max(anchor.x, visible.x);
  return horizontalOverlap >= MIN_ANCHOR_EXTENT && !canScrollIntoView(anchor, visible, reach);
}

/** The bar pinned inside the visible area, at the edge of the band that covers the selection. */
function placePinnedBar(anchor: IRect, visible: IRect, safe: IRect, bar: ISize, gap: number): IPlacement {
  const isCoveredFromAbove: boolean = anchor.y + anchor.height / 2 < visible.y + visible.height / 2;
  const slotY: number = isCoveredFromAbove ? visible.y + gap : visible.y + visible.height - gap - bar.height;
  return {
    mode: "anchored",
    barSide: isCoveredFromAbove ? "stickyTop" : "stickyBottom",
    bar: {
      x: clampX(anchor.x, bar.width, safe),
      y: clampAxis(slotY, bar.height, safe.y, safe.y + safe.height),
      width: bar.width,
      height: bar.height
    },
    edgeChip: null
  };
}

interface IBarSlot {
  side: BarSide;
  y: number;
  fits: boolean;
  slack: number;
}

function buildBarSlots(
  anchor: IRect,
  visible: IRect,
  safe: IRect,
  barHeight: number,
  gap: number,
  anchoredA: IRect
): IBarSlot[] {
  const safeBottom: number = safe.y + safe.height;
  const visibleBottom: number = visible.y + visible.height;

  const safeSlack = (y: number): number => Math.min(y - safe.y, safeBottom - (y + barHeight));
  const visibleSlack = (y: number): number => Math.min(y - visible.y, visibleBottom - (y + barHeight));

  const belowY: number = anchor.y + anchor.height + gap;
  const aboveY: number = anchor.y - gap - barHeight;
  const stickyBottomY: number = Math.min(visibleBottom, anchoredA.y + anchoredA.height) - gap - barHeight;
  const stickyTopY: number = anchoredA.y + gap;

  const stickyBottomEligible: boolean = anchor.y + anchor.height > visibleBottom || anchor.height > visible.height;

  const belowSlack: number = safeSlack(belowY);
  const aboveSlack: number = safeSlack(aboveY);
  const stickyBottomSlack: number = Math.min(safeSlack(stickyBottomY), visibleSlack(stickyBottomY));
  const stickyTopSlack: number = Math.min(safeSlack(stickyTopY), visibleSlack(stickyTopY));

  return [
    { side: "below", y: belowY, fits: belowSlack >= 0, slack: belowSlack },
    { side: "above", y: aboveY, fits: aboveSlack >= 0, slack: aboveSlack },
    {
      side: "stickyBottom",
      y: stickyBottomY,
      fits: stickyBottomEligible && stickyBottomSlack >= 0,
      slack: stickyBottomSlack
    },
    { side: "stickyTop", y: stickyTopY, fits: stickyTopSlack >= 0, slack: stickyTopSlack }
  ];
}

/** Decides where the bar goes for one frame, given its measured size. */
export function placeOverlay(input: IPlacementInput): IPlacement {
  const { visible, safe, bar, gap, prevBarSide, hysteresisBand } = input;
  // A Divider or a hairline border reports a 1px (or 0px) rect; strict intersection would call it
  // off-screen while it sits in plain view. Give it a minimum extent, centred on the element.
  const anchor: IRect = withMinimumExtent(input.anchor, visible);

  const a: IRect | null = intersect(anchor, visible);
  // Only a sliver of a clipped anchor is left (it is scrolling out): point at it instead of anchoring.
  if (a === null || a.height < MIN_ANCHOR_EXTENT) {
    if (input.reach !== undefined && isBehindFixedBand(anchor, visible, input.reach)) {
      return placePinnedBar(anchor, visible, safe, bar, gap);
    }
    const { mode, edgeChip } = placeEdgeChip(anchor, visible, safe);
    return {
      mode,
      barSide: prevBarSide ?? "below",
      bar: null,
      edgeChip
    };
  }

  const barX: number = clampX(a.x, bar.width, safe);
  const slots: IBarSlot[] = buildBarSlots(anchor, visible, safe, bar.height, gap, a);
  const candidates: IBarCandidate[] = slots.map((slot: IBarSlot): IBarCandidate => ({
    side: slot.side,
    fits: slot.fits,
    slack: slot.slack
  }));
  const barSide: BarSide = applyHysteresis(prevBarSide, candidates, hysteresisBand);

  const winningSlot: IBarSlot | undefined = slots.find((slot: IBarSlot): boolean => slot.side === barSide);
  const fallbackY: number = slots[2].y;
  const slotY: number = winningSlot !== undefined && winningSlot.fits ? winningSlot.y : fallbackY;
  // Every anchored bar ends inside `safe` on both axes, whichever slot won (a fitting slot already is).
  const barY: number = clampAxis(slotY, bar.height, safe.y, safe.y + safe.height);
  const barRect: IRect = { x: barX, y: barY, width: bar.width, height: bar.height };

  return {
    mode: "anchored",
    barSide,
    bar: barRect,
    edgeChip: null
  };
}

/**
 * How the bar should get from one placement to the next:
 * - `track` follows the component 1:1 (a scroll, a resize, the same side a pixel on) and must never
 *   animate, since any easing there reads as lag;
 * - `switch` is a discrete jump between two on-screen spots (the bar changes side) and is eased;
 * - `appear` is the bar arriving from off-screen or from the edge chip. It snaps into place, and the
 *   surface's own entry fade softens it. Easing it would slide it in from its parked spot.
 */
export type BarMove = "track" | "switch" | "appear";

export function classifyBarMove(prev: IPlacement | null, next: IPlacement): BarMove {
  if (prev === null || prev.bar === null || next.bar === null || prev.mode !== next.mode) {
    return "appear";
  }
  return prev.barSide === next.barSide ? "track" : "switch";
}
